var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
import {
  Vec,
  bind,
  compact,
  isShapeId
} from "@tldraw/editor";
const SLOW_POINTER_LAG_DURATION = 320;
const FAST_POINTER_LAG_DURATION = 60;
class DragAndDropManager {
  constructor(editor) {
    this.editor = editor;
    editor.disposables.add(this.dispose);
  }
  editor;
  shapesToActuallyMove = [];
  draggedOverShapeIds = /* @__PURE__ */ new Set();
  initialGroupIds = /* @__PURE__ */ new Map();
  initialParentIds = /* @__PURE__ */ new Map();
  initialIndices = /* @__PURE__ */ new Map();
  initialDraggingOverShape;
  prevDraggingOverShape;
  prevPagePoint = new Vec();
  intervalTimerId = -1;
  startDraggingShapes(movingShapes, point, cb) {
    const { editor } = this;
    if (this.intervalTimerId !== -1) return;
    const shapesToActuallyMove = new Set(movingShapes);
    const movingGroups = /* @__PURE__ */ new Set();
    for (const shape of shapesToActuallyMove) {
      const parent = editor.getShapeParent(shape);
      if (parent && editor.isShapeOfType(parent, "group")) {
        if (!movingGroups.has(parent)) {
          movingGroups.add(parent);
        }
      }
    }
    for (const movingGroup of movingGroups) {
      const children = compact(
        editor.getSortedChildIdsForParent(movingGroup).map((id) => editor.getShape(id))
      );
      shapesToActuallyMove.add(movingGroup);
      for (const child of children) {
        shapesToActuallyMove.delete(child);
      }
    }
    this.initialParentIds.clear();
    for (const shape of shapesToActuallyMove) {
      const parent = editor.getShapeParent(shape);
      if (parent) {
        this.initialParentIds.set(shape.id, parent.id);
      }
      this.initialIndices.set(shape.id, shape.index);
      const group = editor.findShapeAncestor(shape, (s) => editor.isShapeOfType(s, "group"));
      if (group) {
        this.initialGroupIds.set(shape.id, group.id);
      }
    }
    const allShapes = editor.getCurrentPageShapesSorted();
    this.shapesToActuallyMove = Array.from(shapesToActuallyMove).filter((s) => !s.isLocked).sort((a, b) => allShapes.indexOf(a) - allShapes.indexOf(b));
    this.initialDraggingOverShape = editor.getDraggingOverShape(point, this.shapesToActuallyMove);
    this.prevDraggingOverShape = void 0;
    this.updateDraggingShapes(point, cb);
    let skip2of3FramesWhileMovingFast = 0;
    this.intervalTimerId = this.editor.timers.setInterval(
      () => {
        skip2of3FramesWhileMovingFast++;
        if (skip2of3FramesWhileMovingFast % 3 && this.editor.inputs.getPointerVelocity().len() > 0.5) {
          return;
        }
        this.updateDraggingShapes(editor.inputs.getCurrentPagePoint(), cb);
      },
      movingShapes.length > 10 ? SLOW_POINTER_LAG_DURATION : FAST_POINTER_LAG_DURATION
    );
  }
  dropShapes(shapes) {
    const { editor } = this;
    const currentPagePoint = editor.inputs.getCurrentPagePoint();
    this.updateDraggingShapes(currentPagePoint);
    const draggingOverShape = editor.getDraggingOverShape(currentPagePoint, shapes);
    if (draggingOverShape) {
      const util = editor.getShapeUtil(draggingOverShape);
      const receivableShapes = getReceivableShapesForTarget(editor, draggingOverShape, shapes);
      if (receivableShapes.length > 0) {
        util.onDropShapesOver?.(draggingOverShape, receivableShapes, {
          initialDraggingOverShapeId: this.initialDraggingOverShape?.id ?? null,
          initialParentIds: this.initialParentIds,
          initialIndices: this.initialIndices
        });
      }
    }
    this.dispose();
  }
  clear() {
    clearInterval(this.intervalTimerId);
    this.intervalTimerId = -1;
    this.initialParentIds.clear();
    this.initialIndices.clear();
    this.shapesToActuallyMove = [];
    this.initialDraggingOverShape = void 0;
    this.prevDraggingOverShape = void 0;
    this.editor.setHintingShapes([]);
  }
  dispose() {
    this.clear();
  }
  updateDraggingShapes(point, cb) {
    const { editor } = this;
    const draggingShapes = compact(this.shapesToActuallyMove.map((s) => editor.getShape(s)));
    if (!draggingShapes.length) return;
    const nextDraggingOverShape = editor.getDraggingOverShape(point, this.shapesToActuallyMove);
    const currentPagePoint = editor.inputs.getCurrentPagePoint();
    const cursorDidMove = !this.prevPagePoint.equals(currentPagePoint);
    this.prevPagePoint.setTo(currentPagePoint);
    editor.run(() => {
      if (this.prevDraggingOverShape?.id === nextDraggingOverShape?.id) {
        if (cursorDidMove && nextDraggingOverShape && isShapeId(nextDraggingOverShape.id) && !editor.inputs.getPreviousPagePoint().equals(currentPagePoint)) {
          const util = editor.getShapeUtil(nextDraggingOverShape);
          util.onDragShapesOver?.(nextDraggingOverShape, draggingShapes, {
            initialDraggingOverShapeId: this.initialDraggingOverShape?.id ?? null,
            initialParentIds: this.initialParentIds,
            initialIndices: this.initialIndices
          });
        }
        return;
      }
      if (this.prevDraggingOverShape) {
        const util = editor.getShapeUtil(this.prevDraggingOverShape);
        const prevDraggingOverShape = this.editor.getShape(this.prevDraggingOverShape);
        const removableShapes = getRemovableShapesForTarget(
          editor,
          prevDraggingOverShape,
          draggingShapes
        );
        if (removableShapes.length > 0) {
          util.onDragShapesOut?.(prevDraggingOverShape, removableShapes, {
            nextDraggingOverShapeId: nextDraggingOverShape?.id ?? null,
            initialDraggingOverShapeId: this.initialDraggingOverShape?.id ?? null,
            initialParentIds: this.initialParentIds,
            initialIndices: this.initialIndices
          });
        }
      }
      if (nextDraggingOverShape) {
        const util = editor.getShapeUtil(nextDraggingOverShape);
        const receivableShapes = getReceivableShapesForTarget(
          editor,
          nextDraggingOverShape,
          draggingShapes
        );
        if (receivableShapes.length > 0) {
          util.onDragShapesIn?.(nextDraggingOverShape, receivableShapes, {
            initialDraggingOverShapeId: this.initialDraggingOverShape?.id ?? null,
            prevDraggingOverShapeId: this.prevDraggingOverShape?.id ?? null,
            initialParentIds: this.initialParentIds,
            initialIndices: this.initialIndices
          });
          editor.setHintingShapes([nextDraggingOverShape.id]);
        } else if (this.prevDraggingOverShape) {
          editor.setHintingShapes([]);
        }
      } else if (this.prevDraggingOverShape) {
        editor.setHintingShapes([]);
      }
      cb?.();
    });
    this.prevDraggingOverShape = nextDraggingOverShape;
  }
}
__decorateClass([
  bind
], DragAndDropManager.prototype, "dispose", 1);
function getReceivableShapesForTarget(editor, targetShape, draggingShapes) {
  const util = editor.getShapeUtil(targetShape);
  return draggingShapes.filter((shape) => util.canReceiveNewChildrenOfType(targetShape, shape.type));
}
function getRemovableShapesForTarget(editor, targetShape, draggingShapes) {
  const util = editor.getShapeUtil(targetShape);
  return draggingShapes.filter((shape) => util.canRemoveChildrenOfType(targetShape, shape.type));
}
export {
  DragAndDropManager
};
//# sourceMappingURL=DragAndDropManager.mjs.map
